import React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {alpha} from "@mui/material/styles";
import {Check, Close} from "@mui/icons-material";
import usePinnedMachineStore from "../../Store/PinnedMachineStore";
import useDisplaySettingsStore from "../../Store/DisplaySettingsStore";

export default function DockPicker({open, onClose, machines}) {
    const pinnedMachineIds = usePinnedMachineStore(state => state.pinnedMachineIds)
    const togglePinnedMachine = usePinnedMachineStore(state => state.togglePinnedMachine)
    const showAllTasksInCards = useDisplaySettingsStore(state => state.showAllTasksInCards)
    const toggleShowAllTasksInCards = useDisplaySettingsStore(state => state.toggleShowAllTasksInCards)

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            PaperProps={{sx: {borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "80vh"}}}
        >
            <Box sx={{display: "flex", alignItems: "center", gap: 2, px: 3, py: 2, borderBottom: 1, borderColor: "divider"}}>
                <Box sx={{flexGrow: 1}}>
                    <Typography variant="h6" fontWeight={600}>Machines affichées en haut</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Touchez une machine pour l'ajouter ou la retirer de la barre
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Fermer" sx={{width: 52, height: 52, bgcolor: "action.hover"}}>
                    <Close/>
                </IconButton>
            </Box>

            <Box
                sx={{
                    p: 3,
                    overflowY: "auto",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                    gap: 1.5,
                }}
            >
                {machines.map(machine => {
                    const pinned = pinnedMachineIds.includes(machine.id)

                    return (
                        <ButtonBase
                            key={machine.id}
                            onClick={() => togglePinnedMachine(machine.id)}
                            aria-pressed={pinned}
                            sx={(theme) => ({
                                justifyContent: "flex-start",
                                gap: 1.5,
                                minHeight: 72,
                                px: 1.5,
                                borderRadius: 2,
                                border: 2,
                                borderColor: pinned ? "primary.main" : "divider",
                                bgcolor: pinned ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
                                textAlign: "left",
                                "&.Mui-focusVisible": {
                                    outline: `3px solid ${theme.palette.primary.main}`,
                                    outlineOffset: 2,
                                },
                            })}
                        >
                            <Typography
                                component="span"
                                sx={{fontSize: 34, fontWeight: 700, lineHeight: 1, minWidth: 48, textAlign: "center", fontVariantNumeric: "tabular-nums"}}
                            >
                                {machine.number}
                            </Typography>
                            <Box component="span" sx={{flexGrow: 1, minWidth: 0}}>
                                <Typography component="span" variant="body2" fontWeight={500} noWrap sx={{display: "block"}}>
                                    {machine.name}
                                </Typography>
                                <Box component="span" sx={{display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", fontSize: 12}}>
                                    <Box
                                        component="span"
                                        sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: machine.power ? "success.main" : "error.main"}}
                                    />
                                    {machine.power ? "Allumée" : "Éteinte"} · {machine.printingTechnique.name}
                                </Box>
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    width: 28,
                                    height: 28,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    display: "grid",
                                    placeItems: "center",
                                    border: 2,
                                    borderColor: pinned ? "primary.main" : "grey.400",
                                    bgcolor: pinned ? "primary.main" : "transparent",
                                    color: "common.white",
                                }}
                            >
                                {pinned && <Check sx={{fontSize: 18}}/>}
                            </Box>
                        </ButtonBase>
                    )
                })}
            </Box>

            <Box sx={{px: 3, py: 1.5, borderTop: 1, borderColor: "divider"}}>
                <FormControlLabel
                    control={<Switch checked={showAllTasksInCards} onChange={toggleShowAllTasksInCards}/>}
                    label="Afficher toutes les tâches dans les cartes (plus lent sur les grosses files)"
                />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{px: 3, pb: 2}}>
                Ces réglages sont enregistrés sur cette tablette uniquement.
            </Typography>
        </Drawer>
    )
}
